import { Select, Button1, TagInput } from "../index";
import refreshIcon from "../../assets/icons/refresh-icon.png";
import downIcon from "../../assets/icons/chevron-down.png";
import circle from "../../assets/icons/Ellipse 81.png";

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { transfersApi } from "../../api/endpoints";
import { newIdempotencyKey } from "../../utils/idempotency";
import TransferOtpModal from "../modals/TransferOtpModal";
import CurrencyFlag from "../currency-flag/CurrencyFlag";

const fmt = (n, c) =>
  `${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${c || ""}`.trim();

const fmtCompact = (n, c) =>
  `${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: Number(n || 0) % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })} ${c || ""}`.trim();

const formatExchangeTime = () =>
  new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

const ExistingRecipientReview = ({ onClick, data }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [otpOpen, setOtpOpen] = useState(false);
  // Stable per review-screen, a retry reuses it, a new transfer gets a new one.
  const [idempotencyKey] = useState(newIdempotencyKey);
  const { sourceAccountId, beneficiaryId, amount, quote, beneficiary, sourceAccount } = data || {};

  const bankAccount = beneficiary?.bankAccounts?.[0] || {};
  const sourceCurrency = sourceAccount?.currency || "AUD";
  const destCurrency = bankAccount.currency || quote?.destCurrency || "USD";
  const sendAmount = Number(amount || 0);
  const balanceBefore = Number(sourceAccount?.availableBalance || 0);
  const fees = Number(quote?.transferFee ?? quote?.fee ?? quote?.fees ?? 0);
  const balanceAfter = balanceBefore - Number(quote?.totalDeducted ?? sendAmount + fees);
  const recipientName = beneficiary?.fullName || beneficiary?.businessName || "Recipient";

  // Create button opens the OTP step; the transfer is only created after the
  // emailed code is verified by the backend.
  const handleCreate = () => {
    setError(null);
    setOtpOpen(true);
  };

  const handleVerifiedCreate = async ({ otpCode, pinCode }) => {
    setLoading(true);
    try {
      await transfersApi.create({
        sourceAccountId,
        beneficiaryId,
        sourceAmount: amount,
        destCurrency: bankAccount.currency || "USD",
        transferType: "INTERNATIONAL",
        otpCode,
        pinCode,
      }, idempotencyKey);
      setOtpOpen(false);
      navigate("/my-transaction");
    } finally {
      setLoading(false);
    }
  };

  const reviewDetails = [
    {
      name: "Bank Name",
      info: bankAccount.bankName || "-",
    },
    {
      name: "SWIFT/BIC Code",
      info: bankAccount.swiftCode || "-",
    },
    {
      name: "Account number",
      info: bankAccount.accountNumber || "-",
    },
    {
      name: "BSB/Routing/Sort Code",
      info: bankAccount.sortCode || bankAccount.routingNumber || bankAccount.bsb || "-",
    },
    {
      name: "IBAN",
      info: bankAccount.iban || "-",
    },
    {
      name: "Country",
      info: beneficiary?.countryCode || bankAccount.country || "-",
    },
    {
      name: "Transfer date",
      info: new Date().toLocaleDateString(),
    },
  ];

  return (
    <div className="existing-recipient-review-shell">
      <div className="existing-recipient-review-rate">
        <div className="existing-recipient-review-rate-meta">
          <span>Exchange rate</span>
          <span>|</span>
          <span>Today at {formatExchangeTime()}</span>
          <img src={refreshIcon} alt="refresh" className="cursor-pointer" />
        </div>
        <div className="existing-recipient-review-rate-value">
          <span>
            1 {sourceCurrency} ={" "}
            <strong>
              {quote?.exchangeRate || "0.00"} {destCurrency}
            </strong>
          </span>
          <img src={downIcon} alt="down" className="cursor-pointer" />
        </div>
      </div>

      <section className="existing-recipient-review-card">
        <h2>Review</h2>

        <div className="existing-recipient-review-summary-grid">
          <div className="existing-recipient-review-field">
            <span>Amount you want to send</span>
            <strong>
              {fmtCompact(sendAmount, sourceCurrency)}
              <CurrencyFlag currency={sourceCurrency} size={18} />
            </strong>
          </div>
          <div className="existing-recipient-review-field">
            <span>Balance After the below transaction</span>
            <strong>{fmtCompact(balanceAfter, sourceCurrency)}</strong>
          </div>
          <div className="existing-recipient-review-field">
            <span>Balance Before the below transaction</span>
            <strong>{fmtCompact(balanceBefore, sourceCurrency)}</strong>
          </div>
          <div className="existing-recipient-review-field">
            <span>Transaction fees</span>
            <strong>{fmt(fees, sourceCurrency)}</strong>
          </div>
        </div>

        <div className="existing-recipient-review-panel">
          <div className="existing-recipient-review-field">
            <span>Net amount recipient gets</span>
            <strong>
              {fmtCompact(quote?.destAmount || 0, destCurrency)}
              <CurrencyFlag currency={destCurrency} size={18} />
            </strong>
          </div>

          <div className="existing-recipient-review-recipient-method">
            <div className="existing-recipient-review-recipient">
              <span className="existing-recipient-review-label">Recipients</span>
              <div className="existing-recipient-review-recipient-person">
                <img src={circle} alt="" />
                <div>
                  <strong>{recipientName}</strong>
                  <span>{beneficiary?.countryCode || "-"}</span>
                </div>
              </div>
            </div>

            <div className="existing-recipient-review-method">
              <span className="existing-recipient-review-label">Method</span>
              <Select
                value="SWIFT"
                options={[{ name: "SWIFT", value: "SWIFT" }]}
                className="existing-recipient-review-select"
                disabled
              />
            </div>
          </div>

          <div className="existing-recipient-review-divider" />

          <div className="existing-recipient-review-detail-list-modern">
            {reviewDetails.map(({ name, info }) => (
              <div key={name}>
                <span>{name}</span>
                <strong>{info}</strong>
              </div>
            ))}
          </div>
        </div>

        <section className="existing-recipient-review-additional">
          <h3>Additional Details</h3>
          <div className="existing-recipient-review-additional-grid">
            <div className="existing-recipient-review-control">
              <label>Category</label>
              <Select
                defaultValue="Choose Category"
                className="existing-recipient-review-select"
              />
            </div>
            <div className="existing-recipient-review-control">
              <label>Tags</label>
              <TagInput
                className="existing-recipient-review-tags"
                inputBgColor="transparent"
                tagColor="#1bbb6b"
                tagTextColor="#000"
                limit={10}
                placeholder="Add up to 10 Tags"
              />
            </div>
          </div>
        </section>

        {error && <div className="existing-recipient-review-error">{error}</div>}

        <div className="existing-recipient-review-actions">
          <Button1
            buttonName="Previous"
            className="border"
            onClick={() => onClick(0)}
            disabled={loading}
          />
          <Button1
            buttonName="Save as Draft"
            className="btn-outline-success"
            disabled={loading}
          />
          <Button1
            buttonName={loading ? "Creating..." : "Create"}
            className="btn-dark"
            onClick={handleCreate}
            disabled={loading || !quote?.hasSufficientBalance}
          />
        </div>
      </section>

      <TransferOtpModal
        open={otpOpen}
        onClose={() => setOtpOpen(false)}
        onVerify={handleVerifiedCreate}
      />
    </div>
  );
};

export default ExistingRecipientReview;
